/**
 * @fileoverview default-help-menu — data-driven default config for HelpPopover.
 * `buildDefaultHelpGroups(callbacks)` accepts optional side-effect callbacks
 * (e.g., onPlatformUpdates to open the WhatsNewDialog) and returns the 4-group
 * 9-entry menu structure that mirrors Foundry's help menu:
 *
 *   1. AIP — "Ask IRIS Assistant" (inline gradient AIP-logo)
 *   2. Documentation — Training / Explore Examples / Platform documentation (external)
 *   3. Support — Contact support / View support tickets
 *   4. Meta — Platform updates (gift icon → WhatsNewDialog) / Status page (external) / About
 *
 * All Blueprint icon names used here (`learning`, `briefcase`, `manual`,
 * `issue-new`, `issue`, `pulse`, `info-sign`) are existing v6 IconName values
 * already used elsewhere in apps/app/src. No `@blueprintjs/icons-*`
 * package imports are introduced.
 */

import { Icon } from "@blueprintjs/core";
import { AipLogoIcon } from "./aip-logo-icon";
import type { HelpMenuGroup } from "./help-popover";
import { WhatsNewGiftIcon } from "./whatsnew-gift-icon";

export interface DefaultHelpMenuCallbacks {
  readonly onAskAip?: () => void;
  readonly onContactSupport?: () => void;
  readonly onPlatformUpdates?: () => void;
  readonly onAbout?: () => void;
}

export function buildDefaultHelpGroups(
  cb: DefaultHelpMenuCallbacks = {},
): HelpMenuGroup[] {
  return [
    {
      id: "aip",
      items: [
        {
          id: "ask-aip-assist",
          label: "Ask IRIS Assistant",
          icon: <AipLogoIcon />,
          onClick: cb.onAskAip,
        },
      ],
    },
    {
      id: "documentation",
      items: [
        {
          id: "training",
          label: "Training",
          icon: <Icon icon="learning" />,
          href: "/training",
        },
        {
          id: "examples",
          label: "Explore Examples",
          icon: <Icon icon="briefcase" />,
          href: "/explore",
        },
        {
          id: "platform-docs",
          label: "Platform documentation",
          icon: <Icon icon="manual" />,
          href: "/docs",
        },
      ],
    },
    {
      id: "support",
      items: [
        {
          id: "contact",
          label: "Contact support",
          icon: <Icon icon="issue-new" />,
          onClick: cb.onContactSupport,
        },
        {
          id: "tickets",
          label: "View support tickets",
          icon: <Icon icon="issue" />,
          href: "/support/tickets",
        },
      ],
    },
    {
      id: "meta",
      items: [
        {
          id: "platform-updates",
          label: "Platform updates",
          icon: <WhatsNewGiftIcon />,
          onClick: cb.onPlatformUpdates,
        },
        {
          id: "status",
          label: "Status page",
          icon: <Icon icon="pulse" />,
          href: "/status",
        },
        {
          id: "about",
          label: "About",
          icon: <Icon icon="info-sign" />,
          onClick: cb.onAbout,
        },
      ],
    },
  ];
}

/**
 * Convenience export — `DEFAULT_HELP_GROUPS` with all callbacks left as
 * undefined. Consumers that need to wire callbacks (e.g., open the
 * WhatsNewDialog from app-shell) should call `buildDefaultHelpGroups({…})`
 * instead.
 */
export const DEFAULT_HELP_GROUPS: HelpMenuGroup[] = buildDefaultHelpGroups();
